/**
 * Mock receipt payloads for the Request flow gallery receipt screen.
 * Keys match `:requestId` route segments and HomePage list item ids.
 */

export type RequestMockReceiptStatus =
  | "pending"
  | "accepted"
  | "funded"
  | "failed"
  | "expired"
  | "cancelled";

export interface RequestMockReceipt {
  requestId: string;
  requestStatus: RequestMockReceiptStatus;
  fundRequestUrl?: string;
  amount?: number;
  destinationCurrency: string;
  createdAt: string;
  /** Payer display name once someone accepted/funded. */
  payerName?: string;
  operationId?: string;
  receiveAmount?: number;
  sendAmount?: number;
  totalCost?: number;
  fee?: number;
  fundingFee?: number;
  transactionFee?: number;
  taxFee?: number;
  totalFee?: number;
  exchangeRate?: number;
  sourceCurrency?: string;
  /** Operation-level status when a fulfilling operation exists. */
  status?: string;
}

/** Default receipt shown when the gallery opens a static route (no :param). */
export const DEFAULT_REQUEST_RECEIPT_ID = "req-gallery-funded";

export const requestMockReceipts: Record<string, RequestMockReceipt> = {
  "req-gallery-funded": {
    requestId: "req-gallery-funded",
    requestStatus: "funded",
    amount: 1742,
    destinationCurrency: "MXN",
    createdAt: "2026-06-09T12:00:00.000Z",
    operationId: "op_gallery_request_1",
    sendAmount: 100,
    receiveAmount: 1742,
    totalCost: 101,
    fee: 1,
    fundingFee: 1,
    transactionFee: 0,
    taxFee: 0,
    exchangeRate: 17.42,
    sourceCurrency: "USD",
    status: "completed",
    payerName: "Jane Doe",
  },
  "req-gallery-pending": {
    requestId: "req-gallery-pending",
    requestStatus: "pending",
    fundRequestUrl: "https://pay.leap.example/fr/gallery-pending",
    amount: 3484,
    destinationCurrency: "MXN",
    createdAt: "2026-06-04T12:00:00.000Z",
  },
  "req-gallery-accepted": {
    requestId: "req-gallery-accepted",
    requestStatus: "accepted",
    amount: 2500,
    destinationCurrency: "MXN",
    createdAt: "2026-06-07T12:00:00.000Z",
    operationId: "op_gallery_request_accepted",
    sendAmount: 143.5,
    receiveAmount: 2500,
    totalCost: 144.5,
    fee: 1,
    fundingFee: 1,
    transactionFee: 0,
    taxFee: 0,
    exchangeRate: 17.42,
    sourceCurrency: "USD",
    status: "processing",
    payerName: "Carlos Mendoza",
  },
  "req-gallery-failed": {
    requestId: "req-gallery-failed",
    requestStatus: "failed",
    amount: 900,
    destinationCurrency: "MXN",
    createdAt: "2026-06-03T12:00:00.000Z",
  },
  "req-gallery-cancelled": {
    requestId: "req-gallery-cancelled",
    requestStatus: "cancelled",
    amount: 600,
    destinationCurrency: "MXN",
    createdAt: "2026-06-02T12:00:00.000Z",
  },
  "req-gallery-expired": {
    requestId: "req-gallery-expired",
    requestStatus: "expired",
    amount: 400,
    destinationCurrency: "MXN",
    createdAt: "2026-06-01T12:00:00.000Z",
  },
};

/**
 * Resolves mock receipt id from react-router param or the last URL segment.
 * Gallery static routes (Control Dock) do not populate useParams — the id is
 * baked into the path instead.
 */
export const resolveRequestReceiptId = (
  paramId: string | undefined,
  pathname: string
): string => {
  if (paramId && requestMockReceipts[paramId]) return paramId;
  const lastSegment = pathname.split("/").filter(Boolean).pop();
  if (lastSegment && requestMockReceipts[lastSegment]) return lastSegment;
  return DEFAULT_REQUEST_RECEIPT_ID;
};

export const getRequestMockReceipt = (
  paramId: string | undefined,
  pathname: string
): RequestMockReceipt =>
  requestMockReceipts[resolveRequestReceiptId(paramId, pathname)] ??
  requestMockReceipts[DEFAULT_REQUEST_RECEIPT_ID];
